import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Slider } from '@/components/ui/slider';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/apiClient';
import { Bell, Users, User, Calendar, Pin } from 'lucide-react';
import type { StaffAlertType } from '@/types/legacy';
import logger from '@/lib/logger';

const alertSchema = z.object({
  alert_type: z.enum(['schedule', 'vacation', 'salary', 'reduction', 'calendar', 'meeting', 'training', 'policy', 'urgent', 'info', 'other']),
  title: z.string().min(3, 'El título debe tener al menos 3 caracteres'),
  message: z.string().min(10, 'El mensaje debe tener al menos 10 caracteres'),
  target_role: z.string().optional(),
  effective_date: z.string().optional(),
  expiry_date: z.string().optional(),
  priority: z.number().min(0).max(10).default(0),
  is_pinned: z.boolean().default(false),
  requires_confirmation: z.boolean().default(false),
});

type AlertFormData = z.infer<typeof alertSchema>;

interface StaffAlertFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

const alertTypeConfig: Record<StaffAlertType, { label: string; icon: string; color: string }> = {
  schedule: { label: 'Horario', icon: '🕐', color: 'blue' },
  vacation: { label: 'Vacaciones', icon: '🏖️', color: 'green' },
  salary: { label: 'Nómina', icon: '💰', color: 'yellow' },
  reduction: { label: 'Reducción Jornada', icon: '⏰', color: 'orange' },
  calendar: { label: 'Calendario', icon: '📅', color: 'purple' },
  meeting: { label: 'Reunión', icon: '👥', color: 'indigo' },
  training: { label: 'Formación', icon: '📚', color: 'cyan' },
  policy: { label: 'Normativa', icon: '📋', color: 'gray' },
  urgent: { label: 'Urgente', icon: '🚨', color: 'red' },
  info: { label: 'Informativo', icon: 'ℹ️', color: 'sky' },
  other: { label: 'Otro', icon: '📌', color: 'slate' },
};

export function StaffAlertForm({ open, onOpenChange, onSuccess }: StaffAlertFormProps) {
  const { user, profile } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [employees, setEmployees] = useState<{ id: string; full_name: string }[]>([]);
  const [targetType, setTargetType] = useState<'all' | 'role' | 'individual'>('all');
  const [selectedEmployee, setSelectedEmployee] = useState<string>('');

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm<AlertFormData>({
    resolver: zodResolver(alertSchema),
    defaultValues: {
      alert_type: 'info',
      priority: 0,
      is_pinned: false,
      requires_confirmation: false,
    },
  });

  const alertType = watch('alert_type');
  const priority = watch('priority');
  const isPinned = watch('is_pinned');
  const requiresConfirmation = watch('requires_confirmation');

  // Cargar lista de empleados
  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const { data } = await api
          .from('profiles')
          .select('id, full_name')
          .order('full_name');
        setEmployees(data || []);
      } catch (error) {
        logger.error('Error fetching employees:', error);
      }
    };
    if (open) fetchEmployees();
  }, [open]);

  const onSubmit = async (data: AlertFormData) => {
    if (!user) return;

    setLoading(true);
    try {
      const alertData = {
        created_by: user.id,
        created_by_name: profile?.full_name || user.email,
        alert_type: data.alert_type,
        title: data.title,
        message: data.message,
        target_employee_id: targetType === 'individual' ? selectedEmployee : null,
        target_role: targetType === 'role' ? data.target_role : (targetType === 'all' ? 'all' : null),
        effective_date: data.effective_date || null,
        expiry_date: data.expiry_date || null,
        priority: data.priority,
        is_pinned: data.is_pinned,
        requires_confirmation: data.requires_confirmation,
      };

      const { error } = await api
        .from('staff_alerts')
        .insert(alertData);

      if (error) throw error;

      toast({
        title: 'Alerta enviada',
        description: 'La comunicación ha sido publicada correctamente',
      });

      reset();
      setTargetType('all');
      setSelectedEmployee('');
      onSuccess();
      onOpenChange(false);
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'No se pudo enviar la alerta',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bell className="w-5 h-5" />
            Nueva Comunicación de Personal
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {/* Tipo de alerta */}
          <div className="space-y-2">
            <Label>Tipo de Comunicación *</Label>
            <Select
              value={alertType}
              onValueChange={(value) => setValue('alert_type', value as StaffAlertType)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Selecciona el tipo" />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(alertTypeConfig).map(([value, config]) => (
                  <SelectItem key={value} value={value}>
                    <span className="flex items-center gap-2">
                      <span>{config.icon}</span>
                      {config.label}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Destinatarios */}
          <div className="space-y-2">
            <Label>Destinatarios *</Label>
            <div className="grid grid-cols-3 gap-2">
              <Button
                type="button"
                variant={targetType === 'all' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setTargetType('all')}
                className="w-full"
              >
                <Users className="w-4 h-4 mr-1" />
                Todos
              </Button>
              <Button
                type="button"
                variant={targetType === 'role' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setTargetType('role')}
                className="w-full"
              >
                Por Rol
              </Button>
              <Button
                type="button"
                variant={targetType === 'individual' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setTargetType('individual')}
                className="w-full"
              >
                <User className="w-4 h-4 mr-1" />
                Individual
              </Button>
            </div>

            {targetType === 'role' && (
              <Select
                value={watch('target_role')}
                onValueChange={(value) => setValue('target_role', value)}
              >
                <SelectTrigger className="mt-2">
                  <SelectValue placeholder="Selecciona rol" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="operator">Operadores</SelectItem>
                  <SelectItem value="supervisor">Supervisores</SelectItem>
                </SelectContent>
              </Select>
            )}

            {targetType === 'individual' && (
              <Select
                value={selectedEmployee}
                onValueChange={setSelectedEmployee}
              >
                <SelectTrigger className="mt-2">
                  <SelectValue placeholder="Selecciona empleado" />
                </SelectTrigger>
                <SelectContent>
                  {employees.map((emp) => (
                    <SelectItem key={emp.id} value={emp.id}>
                      {emp.full_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          {/* Título */}
          <div className="space-y-2">
            <Label htmlFor="title">Título *</Label>
            <Input
              id="title"
              placeholder="Ej: Calendario de vacaciones 2025"
              {...register('title')}
            />
            {errors.title && (
              <p className="text-sm text-destructive">{errors.title.message}</p>
            )}
          </div>

          {/* Mensaje */}
          <div className="space-y-2">
            <Label htmlFor="message">Mensaje *</Label>
            <Textarea
              id="message"
              placeholder="Contenido de la comunicación..."
              rows={4}
              {...register('message')}
            />
            {errors.message && (
              <p className="text-sm text-destructive">{errors.message.message}</p>
            )}
          </div>

          {/* Fechas */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="effective_date">
                <Calendar className="w-4 h-4 inline mr-1" />
                Fecha Efecto
              </Label>
              <Input
                id="effective_date"
                type="date"
                {...register('effective_date')}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="expiry_date">
                <Calendar className="w-4 h-4 inline mr-1" />
                Caduca
              </Label>
              <Input
                id="expiry_date"
                type="date"
                {...register('expiry_date')}
              />
            </div>
          </div>

          {/* Prioridad */}
          <div className="space-y-2">
            <Label>Prioridad: {priority}</Label>
            <Slider
              value={[priority]}
              onValueChange={([value]) => setValue('priority', value)}
              max={10}
              step={1}
              className="py-2"
            />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>Baja</span>
              <span>Media</span>
              <span>Alta</span>
            </div>
          </div>

          {/* Opciones */}
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
              <div className="flex items-center gap-2">
                <Pin className="w-4 h-4" />
                <Label htmlFor="is_pinned" className="cursor-pointer">
                  Fijar en la parte superior
                </Label>
              </div>
              <Switch
                id="is_pinned"
                checked={isPinned}
                onCheckedChange={(checked) => setValue('is_pinned', checked)}
              />
            </div>

            <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
              <div className="flex items-center gap-2">
                <Bell className="w-4 h-4" />
                <Label htmlFor="requires_confirmation" className="cursor-pointer">
                  Requiere confirmación de lectura
                </Label>
              </div>
              <Switch
                id="requires_confirmation"
                checked={requiresConfirmation}
                onCheckedChange={(checked) => setValue('requires_confirmation', checked)}
              />
            </div>
          </div>

          {/* Botones */}
          <div className="flex justify-end gap-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="bg-gradient-magma"
            >
              {loading ? 'Enviando...' : 'Publicar Comunicación'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
